import {
  Form,
  Input,
  Row,
  Col,
  Button,
  DatePicker,
  Tabs,
  Space,
  Table,
  Select,
  TableColumnType,
  Image,
  Tag,
  message,
} from 'antd';
import './index.css';
import { isMobileAtom } from '@/jotai/atoms/userAtom';
import { useAtomValue } from 'jotai';
import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  artworkCertifactionApprove,
  artworkCertificationReject,
  ArtworkVerifyItem,
  ArtworkVerifyListParams,
  ArtworkVerifyStatus,
  getArtworkCampus,
  getArtworkVerifyList,
} from '@/api/artworkVerify';
import { useQuery } from '@tanstack/react-query';
import dayjs from 'dayjs';
import RejectDialog from './RejectDialog';
import { PageLink } from '@/config/pageConfig';
import { FORMAT_DATE, FORMAT_TIME } from '@/constants/date';

const ArtworkVerify = () => {
  const navigate = useNavigate();
  const isMobile = useAtomValue(isMobileAtom);
  const [messageApi, contextHolder] = message.useMessage();
  const [form] =
    Form.useForm<Omit<ArtworkVerifyListParams, 'currentPage' | 'pageSize'>>();

  const [tabKey, setTabKey] = useState('all');
  const [selectedKeys, setSelectedKeys] = useState<string[]>([]);
  const [currentPage, setCurrentPage] = useState(1);
  const pageSize = isMobile ? 10 : 20;
  const [rejectDialogOpen, setRejectDialogOpen] = useState(false);

  const { data: campusList } = useQuery({
    queryKey: ['artwork-campus'],
    queryFn: async () => {
      const resp = await getArtworkCampus();
      if (resp.status !== 200) {
        return [];
      }
      return resp.data.records;
    },
    refetchOnWindowFocus: false,
  });

  const campusOptions = useMemo(() => {
    return campusList?.map((campus) => ({
      value: campus.campusId,
      name: campus.name,
      abbr: campus.abbr,
      label: campus.name,
    }));
  }, [campusList]);

  const tabItems = useMemo(() => {
    return [
      {
        key: 'all',
        label: '汇总',
      },
      {
        key: `${ArtworkVerifyStatus.APPROVED}`,
        label: '认证通过',
      },
      {
        key: `${ArtworkVerifyStatus.REJECTED}`,
        label: '认证驳回',
      },
      {
        key: `${ArtworkVerifyStatus.PENDING}`,
        label: '待认证',
      },
    ];
  }, []);

  const handleTabChange = (key: string) => {
    setTabKey(key);
  };

  const {
    data: verifyList,
    isLoading,
    refetch: refetchVerifyList,
  } = useQuery({
    queryKey: ['artwork-verify-list', currentPage, tabKey],
    queryFn: async () => {
      const values = form.getFieldsValue();
      const params: ArtworkVerifyListParams = {
        reviewKey: tabKey === 'all' ? undefined : Number(tabKey),
        campusCertificationId: values.campusCertificationId,
        suppId: values.suppId,
        fullName: values.fullName,
        email: values.email,
        campusId: values.campusId,
        certificationSubmitDate:
          values.certificationSubmitDate &&
          dayjs(values.certificationSubmitDate).format(FORMAT_DATE),
        reviewUser: values.reviewUser,
        currentPage,
        pageSize,
      };
      const resp = await getArtworkVerifyList(params);
      if (resp.status !== 200) {
        return {
          total: 0,
          records: [],
        };
      }
      return resp.data;
    },
    refetchOnWindowFocus: false,
  });

  const handleSearch = () => {
    setCurrentPage(1);
    void refetchVerifyList();
  };

  const columns: TableColumnType<ArtworkVerifyItem>[] = [
    {
      key: 'campusCertificationId',
      dataIndex: 'campusCertificationId',
      title: '认证ID',
    },
    {
      key: 'suppId',
      dataIndex: 'suppId',
      title: 'SUPP ID',
    },
    {
      key: 'idCardImageUrl',
      dataIndex: 'idCardImageUrl',
      title: '认证图片',
      render: (item: string) => (
        <Image src={item} width={100} />
      ),
    },
    {
      key: 'fullName',
      dataIndex: 'fullName',
      title: '真实姓名',
    },
    {
      key: 'campusName',
      dataIndex: 'campusName',
      title: '学校名',
      render: (item: string) => <span title={item}>{item || '-'}</span>,
    },
    {
      key: 'schoolLocation',
      dataIndex: 'schoolLocation',
      title: '学校所在地区',
      render: (item: string) => <span>{item || '-'}</span>,
    },
    {
      key: 'userRole',
      dataIndex: 'userRole',
      title: '用户身份',
      render: (item: string) => <span>{item || '-'}</span>,
    },
    {
      key: 'reviewStatus',
      dataIndex: 'reviewStatus',
      title: '审核状态',
      render: (_, record) => {
        if (record.reviewKey === ArtworkVerifyStatus.APPROVED) {
          return <Tag color="success">认证通过</Tag>;
        }
        if (record.reviewKey === ArtworkVerifyStatus.REJECTED) {
          return <Tag color="error">认证驳回</Tag>;
        }
        return <Tag>待认证</Tag>;
      },
    },
    {
      key: 'createTime',
      dataIndex: 'createTime',
      title: '认证提交时间',
      render: (item: string) => (
        <span>
          {dayjs(item).isValid() ? dayjs(item).format(FORMAT_TIME) : '-'}
        </span>
      ),
    },
    {
      key: 'reviewTime',
      dataIndex: 'reviewTime',
      title: '审核时间',
      render: (item: string) => (
        <span>
          {dayjs(item).isValid() ? dayjs(item).format(FORMAT_TIME) : '-'}
        </span>
      ),
    },

    {
      key: 'reviewUser',
      dataIndex: 'reviewUser',
      title: '操作人员',
    },
    {
      key: 'action',
      title: '操作',
      fixed: 'right',
      width: 100,
      render: (_, __, index: number) => {
        return (
          <Button type="link" onClick={() => handleVerifyDetailClick(index)}>
            审核操作
          </Button>
        );
      },
    },
  ];

  const handleVerifyDetailClick = (index: number) => {
    const ids =
      verifyList?.records.map((item) => item.campusCertificationId) || [];
    const searchParams = new URLSearchParams();
    searchParams.set('ids', ids.join(','));
    searchParams.set('index', `${index}`);
    navigate(`${PageLink.ArtworkVerifyDetail}?${searchParams.toString()}`);
  };

  const batchActionDisabled = useMemo(() => {
    return !selectedKeys.length;
  }, [selectedKeys]);

  const handleBatchApprove = async () => {
    const resp = await artworkCertifactionApprove(selectedKeys);
    if (resp.status !== 200) {
      messageApi.error('认证通过失败');
      return;
    }
    messageApi.success('认证通过成功');
    setSelectedKeys([]);
    void refetchVerifyList();
  };

  const handleBatchReject = () => {
    setRejectDialogOpen(true);
  };

  const handleRejectConfirm = async (dictKeys: number[], remark: string) => {
    const resp = await artworkCertificationReject({
      campusCertificationIds: selectedKeys,
      reviewRemark: remark,
      dictKeys: dictKeys.join(','),
    });
    if (resp.status !== 200) {
      messageApi.error('已经审核过的作品不能再进行审核');
      return;
    }
    messageApi.success('认证驳回成功');
    setRejectDialogOpen(false);
    setSelectedKeys([]);
    void refetchVerifyList();
  };

  return (
    <div className="artwork-verify-container">
      {contextHolder}
      <Form
        form={form}
        labelCol={{
          span: 8,
        }}
        wrapperCol={{
          span: 16,
        }}
        labelAlign="left"
        onFinish={handleSearch}
      >
        <Row gutter={24}>
          <Col span={12} xs={24} sm={12} md={8}>
            <Form.Item name="campusCertificationId" label="认证ID">
              <Input size="small" />
            </Form.Item>
          </Col>
          <Col span={12} xs={24} sm={12} md={8}>
            <Form.Item name="suppId" label="SUPP ID">
              <Input size="small" />
            </Form.Item>
          </Col>
          <Col span={12} xs={24} sm={12} md={8}>
            <Form.Item name="fullName" label="用户姓名">
              <Input size="small" />
            </Form.Item>
          </Col>
          <Col span={12} xs={24} sm={12} md={8}>
            <Form.Item name="email" label="用户Email">
              <Input size="small" />
            </Form.Item>
          </Col>
          <Col span={12} xs={24} sm={12} md={8}>
            <Form.Item name="campusId" label="学校名称">
              <Select
                size="small"
                style={{ width: '100%' }}
                options={campusOptions}
                optionRender={(option) => (
                  <Space>
                    <span title={option.data.abbr}>{option.data.name}</span>
                  </Space>
                )}
                allowClear
              />
            </Form.Item>
          </Col>
          <Col span={12} xs={24} sm={12} md={8}>
            <Form.Item name="certificationSubmitDate" label="认证提交日期">
              <DatePicker
                size="small"
                style={{ width: '100%' }}
                placeholder=""
                allowClear
              />
            </Form.Item>
          </Col>
          <Col span={12} xs={24} sm={12} md={8}>
            <Form.Item name="reviewUser" label="操作人员">
              <Input size="small" />
            </Form.Item>
          </Col>
        </Row>
        <div>
          <Button type="primary" htmlType="submit">
            搜索
          </Button>
        </div>
      </Form>

      <div className="table-container">
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
          }}
        >
          <Tabs
            type="card"
            size="small"
            items={tabItems}
            activeKey={tabKey}
            onChange={handleTabChange}
            className="tabs-container"
          />
          {!isMobile && (
            <Space size="small">
              <Button
                type="primary"
                disabled={batchActionDisabled}
                onClick={handleBatchApprove}
              >
                通过认证
              </Button>
              <Button
                type="primary"
                danger
                disabled={batchActionDisabled}
                onClick={handleBatchReject}
              >
                驳回认证
              </Button>
            </Space>
          )}
        </div>
        <Table
          size="small"
          loading={isLoading}
          columns={columns}
          dataSource={verifyList?.records || []}
          rowKey="campusCertificationId"
          scroll={{ x: 'max-content' }}
          pagination={{
            size: isMobile ? 'small' : 'default',
            pageSize,
            showSizeChanger: false,
            total: verifyList?.total,
            current: currentPage,
            onChange: (page) => setCurrentPage(page),
          }}
          rowSelection={{
            selectedRowKeys: selectedKeys,
            type: 'checkbox',
            fixed: true,
            onChange: (selectedRowKeys) => {
              setSelectedKeys(selectedRowKeys as string[]);
            },
          }}
        />
      </div>
      <RejectDialog
        isOpen={rejectDialogOpen}
        onClose={() => setRejectDialogOpen(false)}
        onConfirm={handleRejectConfirm}
      />
    </div>
  );
};

export default ArtworkVerify;
